import { useState } from "react";
import { Loader2 } from "lucide-react";

// Hidden page, reached only from the link in the thank-you email. It is
// deliberately absent from the navbar, the footer and the sitemap: devotees
// arrive here because Swami ji's note invited them, not by browsing.
//
// Saves into the same subscriber list the CMS already shows under Newsletter,
// so there is one list of devotees rather than two that drift apart.
export default function StayConnected() {
  const base = import.meta.env.VITE_APP_API_BASE_URL || "";
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    birthday: "",
    anniversary: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      // The newsletter API lives on the CMS; the events base URL already points
      // at that host, so strip the schedule path and post alongside it.
      const url = `${base.replace(/\/scheduleRegistration\/?$/, "")}/newsletter`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          consent: true,
          source: "thank-you-email",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        setError(body?.message || "Something went wrong. Please try again.");
        return;
      }
      setDone(true);
    } catch {
      setError("Could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = (label, key, props = {}) => (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={key} className="text-sm font-medium text-[#4b2418]">
        {label}
      </label>
      <input
        id={key}
        value={form[key]}
        onChange={set(key)}
        disabled={busy}
        /* text-base, never smaller: under 16px iOS Safari zooms the page in
           the moment the field is focused. */
        className="rounded-md border border-[#ddd0b8] bg-[#fffdf9] px-3 py-2.5 text-base text-[#2a1a12] focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30 disabled:bg-gray-100"
        {...props}
      />
    </div>
  );

  return (
    <main className="min-h-screen bg-[#fdf8f1] py-12 text-[#2a1a12]">
      <div className="mx-auto w-full max-w-lg px-5">
        <div className="mb-7 flex items-center gap-3">
          <img
            src="/assets/Avdheshanandg mission logo.png"
            alt="AvdheshanandG Mission"
            className="h-9 w-auto"
          />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#9d1f1c]">
            AvdheshanandG Mission
          </span>
        </div>

        <h1 className="text-2xl font-semibold text-[#5a0000]">
          Stay connected with the AvdheshanandG Mission in the USA
        </h1>

        {done ? (
          <div className="mt-7 rounded-xl border border-green-200 bg-green-50 p-6">
            <p className="text-lg text-green-900">Hari Om, and thank you.</p>
            <p className="mt-2 text-sm leading-7 text-green-900">
              Your details are with us. We will remember you on your special days and let you know
              whenever Swami ji is visiting the United States.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-2 leading-7 text-[#6b5545]">
              Provide us your details with the form below to stay connected about Swami ji&rsquo;s
              programs.
            </p>

            <form
              onSubmit={submit}
              className="mt-7 flex flex-col gap-4 rounded-xl border border-[#e6d6bd] bg-white p-6 shadow-sm"
            >
              {field("Full name", "fullName", { required: true, autoComplete: "name" })}
              {field("Email", "email", { required: true, type: "email", autoComplete: "email" })}
              {field("Phone", "phone", { type: "tel", autoComplete: "tel" })}

              <div className="grid grid-cols-2 gap-4">
                {field("Birthday", "birthday", { placeholder: "DD/MM", inputMode: "numeric" })}
                {field("Anniversary", "anniversary", { placeholder: "DD/MM", inputMode: "numeric" })}
              </div>
              <p className="-mt-1 text-xs leading-6 text-[#6b5545]">
                Birthday and anniversary are optional. Day and month only, so we can send you a
                blessing on the day.
              </p>

              {error && <p className="text-sm text-red-700">{error}</p>}

              <button
                type="submit"
                disabled={busy}
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-md bg-[#5a0000] px-5 py-3 text-sm font-medium text-white hover:bg-[#7a0000] disabled:opacity-60"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                {busy ? "Saving…" : "Keep me connected"}
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-center text-xs leading-6 text-[#6b5545]">
          Any questions? Write to{" "}
          <a
            href="mailto:office@avdheshanandgmission.org"
            className="text-[#9d1f1c] underline-offset-2 hover:underline"
          >
            office@avdheshanandgmission.org
          </a>
          .
        </p>
      </div>
    </main>
  );
}
